import React, { useEffect, useRef, useState } from "react";
import {
  collection,
  getDocs,
  query,
  orderBy,
} from "firebase/firestore";
import { db } from "../firebaseConfig";

const AudioGallery = () => {
  const [audios, setAudios] = useState([]);
  const [loading, setLoading] = useState(true);

  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);

  const [playingId, setPlayingId] = useState(null);

  const audioRefs = useRef({});

  const ITEMS_PER_PAGE = 20;

  // =====================================================
  // FETCH AUDIOS
  // =====================================================

  useEffect(() => {
    const fetchAudios = async () => {
      try {
        setLoading(true);

        const q = query(
          collection(db, "audios"),
          orderBy("timestamp", "desc")
        );

        const snapshot = await getDocs(q);

        const data = snapshot.docs.map((item) => ({
          id: item.id,
          ...item.data(),
        }));

        setAudios(data);
      } catch (error) {
        console.error(
          "Audio gallery load error:",
          error
        );
      } finally {
        setLoading(false);
      }
    };

    fetchAudios();
  }, []);

  // =====================================================
  // SEARCH
  // =====================================================

  const filteredAudios = audios.filter((audio) => {
    const search = searchTerm
      .trim()
      .toLowerCase();

    if (!search) return true;

    return (
      (audio.title || "")
        .toLowerCase()
        .includes(search)
    );
  });

  // =====================================================
  // PAGINATION
  // =====================================================

  const totalPages = Math.ceil(
    filteredAudios.length / ITEMS_PER_PAGE
  );

  const startIndex =
    (currentPage - 1) * ITEMS_PER_PAGE;

  const currentAudios = filteredAudios.slice(
    startIndex,
    startIndex + ITEMS_PER_PAGE
  );

  // =====================================================
  // SEARCH CHANGE
  // =====================================================

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  // =====================================================
  // PLAY / PAUSE
  // =====================================================

  const handlePlay = (id) => {
    const selectedAudio = audioRefs.current[id];

    if (!selectedAudio) return;

    // যদি অন্য audio চলছে
    Object.keys(audioRefs.current).forEach((key) => {
      if (
        key !== id &&
        audioRefs.current[key]
      ) {
        audioRefs.current[key].pause();
        audioRefs.current[key].currentTime = 0;
      }
    });

    if (selectedAudio.paused) {
      selectedAudio
        .play()
        .then(() => {
          setPlayingId(id);
        })
        .catch((error) => {
          console.error(
            "Audio play error:",
            error
          );
        });
    } else {
      selectedAudio.pause();
      setPlayingId(null);
    }
  };

  // =====================================================
  // WHEN AUDIO ENDS
  // =====================================================

  const handleAudioEnded = (id) => {
    if (playingId === id) {
      setPlayingId(null);
    }
  };

  // =====================================================
  // DATE
  // =====================================================

  const formatDate = (timestamp) => {
    if (!timestamp) return "";

    let date;

    if (
      timestamp &&
      typeof timestamp.seconds === "number"
    ) {
      date = new Date(
        timestamp.seconds * 1000
      );
    } else {
      date = new Date(timestamp);
    }

    if (Number.isNaN(date.getTime())) {
      return "";
    }

    return date.toLocaleDateString("bn-BD", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  };

  // =====================================================
  // LOADING
  // =====================================================

  if (loading) {
    return (
      <div
        style={{
          minHeight: "400px",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          color: "#777",
          fontSize: "17px",
        }}
      >
        অডিও লোড হচ্ছে...
      </div>
    );
  }

  // =====================================================
  // RETURN
  // =====================================================

  return (
    <div
      style={{
        maxWidth: "1400px",
        margin: "0 auto",
        padding: "10px",
      }}
    >
      {/* =================================================
          PAGE HEADING + SEARCH
      ================================================= */}

      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: "20px",
          marginBottom: "25px",
          flexWrap: "wrap",
        }}
      >
        <div
          style={{
            borderLeft: "5px solid #d2202a",
            paddingLeft: "12px",
          }}
        >
          <h2
            style={{
              margin: 0,
              fontSize: "25px",
              fontWeight: "bold",
              color: "#333",
            }}
          >
            অডিও
          </h2>
        </div>

        {/* SEARCH */}

        <div
          style={{
            position: "relative",
            width: "280px",
            maxWidth: "100%",
          }}
        >
          <input
            type="text"
            value={searchTerm}
            onChange={handleSearch}
            placeholder="অডিও খুঁজুন..."
            style={{
              width: "100%",
              boxSizing: "border-box",
              padding: "11px 42px 11px 14px",
              border: "1px solid #ddd",
              borderRadius: "4px",
              outline: "none",
              fontSize: "14px",
            }}
          />

          <span
            style={{
              position: "absolute",
              right: "13px",
              top: "50%",
              transform: "translateY(-50%)",
              fontSize: "18px",
              color: "#777",
              pointerEvents: "none",
            }}
          >
            🔍
          </span>
        </div>
      </div>

      {/* =================================================
          NO AUDIO
      ================================================= */}

      {currentAudios.length === 0 ? (
        <div
          style={{
            textAlign: "center",
            padding: "70px 20px",
            color: "#777",
            border: "1px solid #eee",
            borderRadius: "6px",
          }}
        >
          <div
            style={{
              fontSize: "45px",
              marginBottom: "10px",
            }}
          >
            🎧
          </div>

          <div
            style={{
              fontSize: "18px",
              fontWeight: "600",
              marginBottom: "5px",
            }}
          >
            কোনো অডিও পাওয়া যায়নি।
          </div>

          <div
            style={{
              fontSize: "14px",
            }}
          >
            বর্তমানে কোনো অডিও প্রকাশিত হয়নি।
          </div>
        </div>
      ) : (
        <>
          {/* =================================================
              AUDIO GRID
          ================================================= */}

          <div className="audio-gallery-grid">
            {currentAudios.map((audio) => (
              <div
                key={audio.id}
                className="audio-card"
                style={{
                  background: "#fff",
                  border: "1px solid #e2e2e2",
                  borderRadius: "6px",
                  overflow: "hidden",
                  transition:
                    "transform 0.2s ease, box-shadow 0.2s ease",
                }}
              >
                {/* THUMBNAIL */}

                <div
                  style={{
                    position: "relative",
                    width: "100%",
                    aspectRatio: "16 / 9",
                    overflow: "hidden",
                    background: "#eee",
                  }}
                >
                  {audio.thumbnail ? (
                    <img
                      src={audio.thumbnail}
                      alt={audio.title || "অডিও"}
                      loading="lazy"
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                        display: "block",
                      }}
                    />
                  ) : (
                    <div
                      style={{
                        width: "100%",
                        height: "100%",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        background:
                          "linear-gradient(135deg, #555, #222)",
                        color: "#fff",
                        fontSize: "55px",
                      }}
                    >
                      🎧
                    </div>
                  )}

                  {/* DARK OVERLAY */}

                  <div
                    style={{
                      position: "absolute",
                      inset: 0,
                      background:
                        playingId === audio.id
                          ? "rgba(0,0,0,0.35)"
                          : "rgba(0,0,0,0.12)",
                      transition: "all 0.2s ease",
                    }}
                  />

                  {/* PLAY BUTTON */}

                  <button
                    type="button"
                    onClick={() =>
                      handlePlay(audio.id)
                    }
                    aria-label={
                      playingId === audio.id
                        ? "Pause audio"
                        : "Play audio"
                    }
                    style={{
                      position: "absolute",
                      left: "50%",
                      top: "50%",
                      transform:
                        "translate(-50%, -50%)",
                      width: "62px",
                      height: "62px",
                      borderRadius: "50%",
                      border: "none",
                      background:
                        "rgba(210,32,42,0.95)",
                      color: "#fff",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      cursor: "pointer",
                      fontSize: "25px",
                      paddingLeft:
                        playingId === audio.id
                          ? "0"
                          : "4px",
                      boxShadow:
                        "0 4px 15px rgba(0,0,0,0.3)",
                      transition:
                        "transform 0.2s ease, background 0.2s ease",
                      zIndex: 2,
                    }}
                  >
                    {playingId === audio.id
                      ? "❚❚"
                      : "▶"}
                  </button>

                  {/* HIDDEN AUDIO */}

                  <audio
                    ref={(element) => {
                      audioRefs.current[
                        audio.id
                      ] = element;
                    }}
                    src={audio.audioUrl}
                    preload="none"
                    onEnded={() =>
                      handleAudioEnded(
                        audio.id
                      )
                    }
                    onPause={() => {
                      if (
                        playingId ===
                        audio.id
                      ) {
                        setPlayingId(null);
                      }
                    }}
                    style={{
                      display: "none",
                    }}
                  />
                </div>

                {/* CONTENT */}

                <div
                  style={{
                    padding: "13px 14px 15px",
                  }}
                >
                  <h3
                    style={{
                      margin: 0,
                      fontSize: "18px",
                      lineHeight: 1.5,
                      fontWeight: "700",
                      color: "#222",
                      display: "-webkit-box",
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: "vertical",
                      overflow: "hidden",
                    }}
                  >
                    {audio.title}
                  </h3>

                  {/* DATE */}

                  {audio.timestamp && (
                    <div
                      style={{
                        marginTop: "8px",
                        fontSize: "13px",
                        color: "#888",
                      }}
                    >
                      {formatDate(
                        audio.timestamp
                      )}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* =================================================
              PAGINATION
          ================================================= */}

          {totalPages > 1 && (
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                gap: "6px",
                marginTop: "35px",
                flexWrap: "wrap",
              }}
            >
              {/* PREVIOUS */}

              <button
                type="button"
                disabled={currentPage === 1}
                onClick={() => {
                  setCurrentPage((prev) =>
                    Math.max(1, prev - 1)
                  );

                  window.scrollTo({
                    top: 0,
                    behavior: "smooth",
                  });
                }}
                style={{
                  width: "38px",
                  height: "36px",
                  border: "1px solid #ddd",
                  borderRadius: "4px",
                  background:
                    currentPage === 1
                      ? "#f5f5f5"
                      : "#fff",
                  color:
                    currentPage === 1
                      ? "#aaa"
                      : "#333",
                  cursor:
                    currentPage === 1
                      ? "not-allowed"
                      : "pointer",
                  fontSize: "20px",
                }}
              >
                ‹
              </button>

              {/* PAGE NUMBERS */}

              {Array.from(
                { length: totalPages },
                (_, index) => index + 1
              ).map((page) => (
                <button
                  type="button"
                  key={page}
                  onClick={() => {
                    setCurrentPage(page);

                    window.scrollTo({
                      top: 0,
                      behavior: "smooth",
                    });
                  }}
                  style={{
                    minWidth: "36px",
                    height: "36px",
                    padding: "0 8px",
                    border:
                      currentPage === page
                        ? "1px solid #d2202a"
                        : "1px solid #ddd",
                    borderRadius: "4px",
                    background:
                      currentPage === page
                        ? "#d2202a"
                        : "#fff",
                    color:
                      currentPage === page
                        ? "#fff"
                        : "#333",
                    cursor: "pointer",
                    fontWeight:
                      currentPage === page
                        ? "bold"
                        : "normal",
                    fontSize: "14px",
                  }}
                >
                  {page}
                </button>
              ))}

              {/* NEXT */}

              <button
                type="button"
                disabled={
                  currentPage === totalPages
                }
                onClick={() => {
                  setCurrentPage((prev) =>
                    Math.min(
                      totalPages,
                      prev + 1
                    )
                  );

                  window.scrollTo({
                    top: 0,
                    behavior: "smooth",
                  });
                }}
                style={{
                  width: "38px",
                  height: "36px",
                  border: "1px solid #ddd",
                  borderRadius: "4px",
                  background:
                    currentPage === totalPages
                      ? "#f5f5f5"
                      : "#fff",
                  color:
                    currentPage === totalPages
                      ? "#aaa"
                      : "#333",
                  cursor:
                    currentPage === totalPages
                      ? "not-allowed"
                      : "pointer",
                  fontSize: "20px",
                }}
              >
                ›
              </button>
            </div>
          )}
        </>
      )}

      {/* =================================================
          RESPONSIVE CSS
      ================================================= */}

      <style>{`
        .audio-gallery-grid {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 20px;
        }

        .audio-card:hover {
          transform: translateY(-3px);
          box-shadow: 0 5px 18px rgba(0,0,0,0.10);
        }

        @media (max-width: 1100px) {
          .audio-gallery-grid {
            grid-template-columns: repeat(3, 1fr);
          }
        }

        @media (max-width: 800px) {
          .audio-gallery-grid {
            grid-template-columns: repeat(2, 1fr);
            gap: 15px;
          }
        }

        @media (max-width: 550px) {
          .audio-gallery-grid {
            grid-template-columns: 1fr;
            gap: 18px;
          }
        }
      `}</style>
    </div>
  );
};

export default AudioGallery;